import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Badge, Tabs, Select, Switch, Dialog, Input, Textarea, Alert } from '@dreamtree-org/twreact-ui';
import { PageHeader, SectionCard, StatusBadge, QueryState } from '@/components';
import { useQuery, useMutation } from '@/lib/useQuery';
import DataTable from '../components/DataTable';
import { diffPolicy, summarizeDiff, formatPolicyValue, flatten, unflatten, coerceLike, nextVersion } from '../lib/policyDiff';
import { formatDateTime } from '../lib/format';
import { usePlatformCan } from '../lib/access';

const CHANGE = { added: ['success', 'Added'], removed: ['error', 'Removed'], changed: ['warning', 'Changed'], unchanged: ['default', 'Same'] };

/**
 * ADMIN-05 Policy & configuration: version-controlled commission, tax/provider, returns and risk rules.
 * Publishing (policies.publish) supersedes the active version; the server refuses an identical body
 * and settings outside their range. Orders snapshot the fee policy, so a change never re-prices a sale.
 * A pending version (MERCH-88, seeded by a migration: e.g. GST slabs by product type) is never live: it is
 * shown apart, and "Review" opens a draft with its settings added; publishing that adopts it.
 */
export default function Policies() {
  const query = useQuery('platformPolicies', undefined, { select: (r) => r.data });
  const [params, setParams] = useSearchParams();

  return (
    <div className="space-y-6">
      <PageHeader title="Policies & configuration" subtitle="ADMIN-05 · every change is a new version with a note, an author and a diff" />
      <QueryState query={query}>
        {(policies) => {
          const idx = Math.max(0, policies.findIndex((p) => p.key === params.get('policy')));
          const setIdx = (i) => setParams({ policy: policies[i].key }, { replace: true });
          return (
            <>
              <Tabs index={idx} onChange={setIdx} orientation="horizontal" variant="line" animated={false}>
                <Tabs.List className="overflow-x-auto whitespace-nowrap border-b border-[var(--app-border)]">
                  {policies.map((p) => <Tabs.Tab key={p.key} className="min-h-[44px]">{p.name}</Tabs.Tab>)}
                </Tabs.List>
              </Tabs>
              <PolicyPanel
                key={policies[idx].key}
                policy={policies[idx]}
                onPublished={(p) => query.setData((all) => all.map((x) => (x.key === p.key ? p : x)))}
              />
            </>
          );
        }}
      </QueryState>
    </div>
  );
}

function PolicyPanel({ policy, onPublished }) {
  const canPublish = usePlatformCan('policies.publish');
  const versions = policy.versions;
  const active = versions[versions.length - 1];
  const [from, setFrom] = useState(versions.length > 1 ? versions[versions.length - 2].version : active.version);
  const [to, setTo] = useState(active.version);
  const [showSame, setShowSame] = useState(false);
  const [drafting, setDrafting] = useState(null); // the body the draft starts from
  const pending = policy.pending;
  const upcoming = policy.next_version || nextVersion(active.version);
  const byVersion = Object.fromEntries(versions.map((v) => [v.version, v]));
  const diff = useMemo(() => diffPolicy(byVersion[from] && byVersion[from].body, byVersion[to] && byVersion[to].body), [from, to, policy]);
  const summary = summarizeDiff(diff);
  const options = versions.map((v) => ({ value: v.version, label: `${v.version} · ${formatDateTime(v.published_at)}` }));

  return (
    <div className="space-y-4">
      <SectionCard
        title={<span className="flex flex-wrap items-center gap-2">{policy.name} <Badge variant="success" size="sm">active {active.version}</Badge></span>}
        actions={canPublish ? <Button onClick={() => setDrafting(active.body)}>Draft {upcoming}</Button> : null}
      >
        <p className="text-sm opacity-80">{policy.description}</p>
        {policy.applies ? <p className="text-sm"><span className="font-medium">Takes effect:</span> {policy.applies}</p> : null}
        <p className="text-xs opacity-60">Published {formatDateTime(active.published_at)} by {active.author} — “{active.note}”</p>
      </SectionCard>

      {pending ? (
        <Alert variant="warning" title={`Pending ${pending.version}: not live`}>
          <p className="text-sm">{pending.note} — proposed by {pending.author}. Nothing reads it until it is published.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {diffPolicy(active.body, pending.proposed_body).filter((d) => d.change === 'added').slice(0, 6).map((d) => <Badge key={d.path} variant="warning" size="sm">{d.path}</Badge>)}
          </div>
          {canPublish ? <div className="mt-3"><Button size="sm" onClick={() => setDrafting(pending.proposed_body)}>Review and publish as {upcoming}</Button></div> : null}
        </Alert>
      ) : null}

      <SectionCard title="Compare versions">
        <div className="grid gap-3 sm:grid-cols-3 items-end">
          <Select label="Before" options={options} value={from} onChange={setFrom} closeOnSelect />
          <Select label="After" options={options} value={to} onChange={setTo} closeOnSelect />
          <Switch checked={showSame} onChange={setShowSame} rightLabel="Show unchanged" ariaLabel="Show unchanged keys" />
        </div>
        <div className="flex flex-wrap gap-2 text-sm">
          {['changed', 'added', 'removed', 'unchanged'].map((k) => <Badge key={k} variant={CHANGE[k][0]} size="sm">{summary[k]} {CHANGE[k][1].toLowerCase()}</Badge>)}
        </div>
        <DiffTable rows={showSame ? diff : diff.filter((d) => d.change !== 'unchanged')} />
      </SectionCard>

      <SectionCard title="Version history">
        <DataTable
          data={[...versions].reverse()}
          columns={[
            { key: 'version', label: 'Version', render: (v) => <span className="font-medium">{v.version}</span> },
            { key: 'status', label: 'Status', render: (v) => <StatusBadge status={v.status} /> },
            { key: 'note', label: 'Change note', wrap: true },
            { key: 'author', label: 'Author', hideOnMobile: true },
            { key: 'published_at', label: 'Published', hideOnMobile: true, render: (v) => formatDateTime(v.published_at) },
          ]}
        />
      </SectionCard>

      {drafting ? <DraftDialog policy={policy} active={active} start={drafting} upcoming={upcoming} onClose={() => setDrafting(null)} onPublished={(p) => { onPublished(p); setFrom(active.version); setTo(upcoming); }} /> : null}
    </div>
  );
}

function DiffTable({ rows }) {
  if (!rows.length) return <p className="text-sm opacity-70">No differences between these versions.</p>;
  return (
    <DataTable
      data={rows}
      sortable={false}
      columns={[
        { key: 'path', label: 'Setting', render: (d) => <code className="text-xs break-all">{d.path}</code> },
        { key: 'change', label: 'Change', render: (d) => <Badge variant={CHANGE[d.change][0]} size="sm">{CHANGE[d.change][1]}</Badge> },
        { key: 'before', label: 'Before', wrap: true, render: (d) => <span className={d.change === 'removed' || d.change === 'changed' ? 'line-through opacity-70' : ''}>{formatPolicyValue(d.before)}</span> },
        { key: 'after', label: 'After', wrap: true, render: (d) => <span className={d.change === 'added' || d.change === 'changed' ? 'font-semibold' : ''}>{formatPolicyValue(d.after)}</span> },
      ]}
    />
  );
}

function DraftDialog({ policy, active, start, upcoming, onClose, onPublished }) {
  const publish = useMutation('platformPolicyPublish');
  const base = flatten(start || active.body);
  const [values, setValues] = useState(Object.fromEntries(Object.entries(base).map(([k, v]) => [k, Array.isArray(v) ? v.join(', ') : String(v)])));
  const [note, setNote] = useState('');

  const parsed = Object.fromEntries(Object.entries(values).map(([k, s]) => [k, coerceLike(s, base[k])]));
  const errors = Object.fromEntries(Object.entries(parsed).filter(([, r]) => !r.ok).map(([k, r]) => [k, r.message]));
  const body = unflatten(Object.fromEntries(Object.entries(parsed).map(([k, r]) => [k, r.ok ? r.value : base[k]])));
  const changes = diffPolicy(active.body, body).filter((d) => d.change !== 'unchanged');
  const canPublish = !Object.keys(errors).length && changes.length > 0 && note.trim();

  const submit = async () => {
    try {
      const r = await publish.run({ key: policy.key, body, note: note.trim() });
      onPublished(r.data);
      onClose();
    } catch {
      /* publish.error shown below */
    }
  };

  return (
    <Dialog
      isOpen
      onClose={onClose}
      size="lg"
      title={`${policy.name} — draft ${upcoming}`}
      footer={(
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={publish.loading} disabled={!canPublish}>Publish {upcoming}</Button>
        </div>
      )}
    >
      <div className="space-y-4">
        {publish.error ? <Alert variant="error" title="Not published">{publish.error}</Alert> : null}
        <div className="grid gap-3 sm:grid-cols-2">
          {Object.keys(base).map((k) => (
            <Input key={k} label={k} value={values[k]} onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))} error={errors[k]} helperText={Array.isArray(base[k]) ? 'Comma-separated' : undefined} />
          ))}
        </div>
        <Textarea label="Change note (required)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={200} showCount />
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Diff against active {active.version}</h3>
          <DiffTable rows={changes} />
        </div>
      </div>
    </Dialog>
  );
}
